/**
 * Waits until the ATX SDK has its own designed UI on screen.
 *
 * This is what lets this client stay put until then. ATX mounts long before it
 * has anything worth showing: for the first half second the store is still
 * bootstrapping and all it renders is an unstyled "Initializing ATX
 * application" line, and its layout shell follows before the splash. Handing
 * the screen over on "ATX mounted" shows the candidate those states.
 *
 * ATX renders into an open shadow root, so it can be observed from here.
 * Measured on a warm local machine: mount ~0.5s, splash ~2.5s, first flow
 * screen ~17s — so the splash, which carries a progress indicator, is the
 * right point to hand over.
 */

/** ATX's shadow host, created by its `ShadowWrapper`. */
const ATX_SHADOW_HOST_ID = "widget-root";

/** ATX's React mount point inside the shadow root. */
const ATX_APP_CONTAINER_ID = "embed-app-container";

/**
 * The splash is an image plus a progress indicator, and every flow screen
 * offers the candidate a control; ATX's bootstrap states have neither. Keying
 * on rendered elements rather than copy keeps this independent of wording.
 */
const RENDERED_UI_SELECTOR =
  "img, [role=\"progressbar\"], progress, button, input, select, textarea, a[href], [role=\"button\"]";

/**
 * Generous: ATX bootstraps its store, fetches runtime config, signs into
 * Firebase and runs compatibility checks before its first screen, which is
 * seconds on a slow connection.
 */
const UI_READY_TIMEOUT_MS = 90000;

const POLL_MS = 50;

const isAtxUiRendered = (): boolean => {
  const app = document
    .getElementById(ATX_SHADOW_HOST_ID)
    ?.shadowRoot?.getElementById(ATX_APP_CONTAINER_ID);
  return !!app?.querySelector(RENDERED_UI_SELECTOR);
};

/**
 * Resolves true once ATX has rendered, false if it never does within the
 * deadline. Polled rather than observed: the shadow host does not exist yet
 * when this starts, so there is nothing to attach an observer to.
 */
export const waitForAtxUi = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (isAtxUiRendered()) {
      resolve(true);
      return;
    }

    const settle = (rendered: boolean) => {
      clearInterval(poll);
      clearTimeout(deadline);
      resolve(rendered);
    };
    const poll = setInterval(() => isAtxUiRendered() && settle(true), POLL_MS);
    const deadline = setTimeout(() => settle(false), UI_READY_TIMEOUT_MS);
  });

export const uiReadyTimeoutMs = UI_READY_TIMEOUT_MS;
